import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { getRCTIDetails, updateRCTILineItem, sendDraftRCTIToXero, syncRCTIWithXero, voidRCTI } from '../services/rctiService';
import { getSignedUrl } from '../services/fileService';
import { FaFileInvoiceDollar, FaArrowLeft, FaPaperPlane, FaSync, FaFilePdf, FaBan } from 'react-icons/fa';
import ConfirmationModal from '../components/ConfirmationModal';
import { isDemoMode } from '../utils/demoMode';

// Helper to format Firestore timestamp to a readable date string
const formatDate = (timestamp) => {
  if (!timestamp || !timestamp._seconds) return 'N/A';
  return new Date(timestamp._seconds * 1000).toLocaleDateString();
};

// Helper to format Firestore timestamp to a readable date and time string
const formatDateTime = (timestamp) => {
    if (!timestamp || !timestamp._seconds) return 'N/A';
    return new Date(timestamp._seconds * 1000).toLocaleString();
  };

const parseNumberInput = (value) => {
  if (value === '' || value === null || value === undefined) {
    return null;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const RCTIDetailsPage = () => {
  const { rctiId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnSearch = location.state?.returnSearch || '';
  const [searchParams] = useSearchParams();
  const [rcti, setRCTI] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isVoiding, setIsVoiding] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isEditingLineItem, setIsEditingLineItem] = useState(false);
  const [showVoidConfirm, setShowVoidConfirm] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [sendToXeroError, setSendToXeroError] = useState(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [lineItemForm, setLineItemForm] = useState({
    Quantity: '',
    UnitAmount: '',
  });

  const fetchRCTIData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getRCTIDetails(rctiId);
      
      // Log the received data to help debug field issues
      console.log('🔍 RCTI Data received in RCTIDetailsPage:', {
        rctiId,
        data,
        allKeys: Object.keys(data || {}),
        contactRelatedFields: {
          ContactName: data?.ContactName,
          EmailAddress: data?.EmailAddress,
          XeroContactID: data?.XeroContactID, // Capital ID
          XeroContactId: data?.XeroContactId, // Lowercase d
          ContactEmail: data?.ContactEmail,
          // Check other possible variations
          contactName: data?.contactName,
          emailAddress: data?.emailAddress,
          xeroContactId: data?.xeroContactId,
          contactEmail: data?.contactEmail,
        },
        addressFields: {
          POAddressLine1: data?.POAddressLine1,
          POAddressLine2: data?.POAddressLine2,
          POCity: data?.POCity,
          PORegion: data?.PORegion,
          POPostalCode: data?.POPostalCode,
          POCountry: data?.POCountry,
        }
      });
      
      setRCTI(data);
    } catch (err) {
      setError(err.message || 'Failed to load RCTI details.');
      setRCTI(null);
    } finally {
      setLoading(false);
    }
  }, [rctiId]);

  useEffect(() => {
    setSuccessMessage('');
    setSendToXeroError(null);
    fetchRCTIData();
  }, [fetchRCTIData, rctiId]);

  useEffect(() => {
    if (!rcti) {
      return;
    }

    setLineItemForm({
      Quantity: rcti.Quantity ?? '',
      UnitAmount: rcti.UnitAmount ?? '',
    });
  }, [rcti]);

  const handleSendToXero = async () => {
    if (!rcti || rcti.status !== 'draft') {
      setSendToXeroError("RCTI is not in draft status or not loaded.");
      return;
    }
    setIsSending(true);
    setSuccessMessage('');
    setSendToXeroError(null);

    let sendAttemptSucceeded = false;
    let operationMessage = '';

    try {
      const response = await sendDraftRCTIToXero(rcti.id);
      sendAttemptSucceeded = true;
      operationMessage = response.message || "RCTI successfully sent to Xero and status updated.";
    } catch (err) {
      sendAttemptSucceeded = false;
      operationMessage = err.message || 'Failed to send RCTI to Xero.';
    }

    // Attempt to refresh RCTI data
    try {
      const refreshedData = await getRCTIDetails(rctiId);
      setRCTI(refreshedData);
    } catch (refreshError) {
      console.error("Failed to refresh RCTI data after Xero operation:", refreshError);
    }

    if (sendAttemptSucceeded) {
      setSuccessMessage(operationMessage);
      setSendToXeroError(null);
    } else {
      setSendToXeroError(operationMessage);
      setSuccessMessage('');
    }

    setIsSending(false);
  };

  const handleSyncFromXero = async () => {
    if (!rcti?.id) return;

    setIsSyncing(true);
    setSendToXeroError(null);
    setSuccessMessage('');

    try {
      const response = await syncRCTIWithXero(rcti.id);
      console.log('Xero sync response:', response);

      if (response.status === 'success') {
        if (response.data.updatedFields?.length > 0) {
          const updatedRCTI = await getRCTIDetails(rcti.id);
          setRCTI(updatedRCTI);
        }
        setSuccessMessage(response.message);
      } else {
        setSendToXeroError(response.message);
      }
    } catch (err) {
      console.error('Error syncing from Xero:', err);
      setSendToXeroError(err.message || 'Failed to sync from Xero');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleVoidRCTI = async () => {
    if (!rcti?.id) return;

    setIsVoiding(true);
    setSendToXeroError(null);
    setSuccessMessage('');
    setShowVoidConfirm(false);

    try {
      const response = await voidRCTI(rcti.id);
      console.log('Void RCTI response:', response);

      if (response.status === 'success') {
        // Refresh RCTI data to show updated status
        const updatedRCTI = await getRCTIDetails(rcti.id);
        setRCTI(updatedRCTI);
        setSuccessMessage(response.message);
      } else {
        setSendToXeroError(response.message || 'Failed to void RCTI');
      }
    } catch (err) {
      console.error('Error voiding RCTI:', err);
      setSendToXeroError(err.message || 'Failed to void RCTI');
    } finally {
      setIsVoiding(false);
    }
  };

  const handleViewPdf = async () => {
    if (!rcti?.pdfDownloadUrl) return;
    try {
      setIsLoadingPdf(true);
      const signedUrl = await getSignedUrl(rcti.pdfDownloadUrl);
      window.open(signedUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Error opening PDF:', err);
      setError('Failed to open PDF: ' + err.message);
    } finally {
      setIsLoadingPdf(false);
    }
  };

  const handleLineItemInputChange = (event) => {
    const { name, value } = event.target;

    setLineItemForm((currentForm) => ({
      ...currentForm,
      [name]: value,
    }));
  };

  const handleCancelLineItemEdit = () => {
    if (rcti) {
      setLineItemForm({
        Quantity: rcti.Quantity ?? '',
        UnitAmount: rcti.UnitAmount ?? '',
      });
    }

    setIsEditingLineItem(false);
    setSendToXeroError(null);
  };

  const handleSaveLineItem = async () => {
    if (!rcti?.id) {
      return;
    }

    const quantity = parseNumberInput(lineItemForm.Quantity);
    const unitAmount = parseNumberInput(lineItemForm.UnitAmount);

    if (quantity === null || quantity <= 0) {
      setSendToXeroError('Quantity must be greater than 0.');
      return;
    }

    if (unitAmount === null || unitAmount < 0) {
      setSendToXeroError('Unit Amount must be 0 or greater.');
      return;
    }

    setIsUpdating(true);
    setSuccessMessage('');
    setSendToXeroError(null);

    try {
      const response = await updateRCTILineItem(rcti.id, {
        Quantity: quantity,
        UnitAmount: unitAmount,
      });
      const refreshedRCTI = await getRCTIDetails(rcti.id);
      setRCTI(refreshedRCTI);
      setIsEditingLineItem(false);
      setSuccessMessage(response.message || 'RCTI line item updated successfully.');
    } catch (err) {
      console.error('Error updating RCTI line item:', err);
      setSendToXeroError(err.message || 'Failed to update RCTI line item.');
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading && !isSending && !rcti) {
    return <p className="text-center text-gray-500 py-10">Loading RCTI details...</p>;
  }

  if (error && !rcti) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <p className="text-red-500 text-lg">Error: {error}</p>
        <Link to={{ pathname: '/invoices', search: returnSearch || '?tab=rctis' }} className="mt-4 inline-block bg-jims-cyan hover:bg-jims-cyan-dark text-white font-bold py-2 px-4 rounded">
          <FaArrowLeft className="mr-2 inline" /> Back to Invoices
        </Link>
      </div>
    );
  }

  if (!rcti && !loading) {
    return <p className="text-center text-gray-500 py-10">RCTI not found.</p>;
  }
  
  const displayQuantity = isEditingLineItem ? parseNumberInput(lineItemForm.Quantity) : rcti?.Quantity;
  const displayUnitAmount = isEditingLineItem ? parseNumberInput(lineItemForm.UnitAmount) : rcti?.UnitAmount;

  const amountDue = displayQuantity != null && displayUnitAmount != null
    ? (displayQuantity * displayUnitAmount).toFixed(2)
    : 'N/A';

  const displayStatus = rcti?.xero_submission_status === 'success' && rcti?.xero_invoice_status 
                        ? rcti.xero_invoice_status 
                        : rcti?.status;

  let statusHighlight = 'gray';
  if (rcti?.status === 'void' || rcti?.xero_invoice_status === 'VOIDED') {
    statusHighlight = 'red';
  } else if (rcti?.xero_submission_status === 'success' && rcti?.xero_invoice_status) {
    const xeroStatusLower = rcti.xero_invoice_status.toLowerCase();
    if (xeroStatusLower === 'paid') {
      statusHighlight = 'green';
    } else if (xeroStatusLower === 'authorised' || xeroStatusLower === 'submitted') {
      statusHighlight = 'blue';
    } else if (xeroStatusLower === 'draft') {
      statusHighlight = 'yellow';
    }
  } else if (rcti?.status) {
    const systemStatusLower = rcti.status.toLowerCase();
    if (systemStatusLower === 'paid') {
      statusHighlight = 'green';
    } else if (systemStatusLower === 'draft') {
      statusHighlight = 'yellow';
    }
  }

  // Determine if RCTI can be voided (not already void, and not paid in Xero)
  const canVoid = rcti &&
                  rcti.status !== 'void' &&
                  rcti.xero_invoice_status !== 'VOIDED' &&
                  rcti.xero_invoice_status !== 'PAID';
  const canEditLineItem = rcti &&
                          rcti.status === 'draft' &&
                          rcti.status !== 'void' &&
                          !rcti.xeroInvoiceId &&
                          rcti.xero_submission_status !== 'success';

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <FaFileInvoiceDollar className="mr-3" /> RCTI Details
        </h1>
        <div className="flex items-center">
            <button 
                onClick={fetchRCTIData} 
                className="text-gray-900 hover:text-green-600 mr-4 p-2 rounded-full hover:bg-gray-200 transition duration-150"
                title="Refresh RCTI Data"
                disabled={loading || isSending || isUpdating}
            >
                <FaSync className={` ${loading && !isSending ? 'animate-spin' : ''}`} />
            </button>
            <button 
                onClick={() => {
                  if (returnSearch) {
                    navigate({ pathname: '/invoices', search: returnSearch });
                  } else {
                    const returnTab = searchParams.get('returnTab');
                    if (returnTab === 'rctis') {
                      navigate('/invoices?tab=rctis');
                    } else {
                      navigate(-1);
                    }
                  }
                }} 
                className="text-gray-900 hover:text-green-600 underline flex items-center"
            >
                <FaArrowLeft className="mr-2" /> Back
            </button>
        </div>
      </div>

      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 border border-green-300 rounded">
          {successMessage}
        </div>
      )}

      {sendToXeroError && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded">
          Error: {sendToXeroError}
        </div>
      )}

      {!isDemoMode() && rcti && rcti.status === 'draft' && (
        <div className="mb-6 flex justify-end space-x-4">
          <button
            onClick={handleSendToXero}
            disabled={isSending || loading || isUpdating || isEditingLineItem || rcti.xero_submission_status === 'success'}
            className={`bg-jims-green hover:bg-jims-green-dark text-white font-bold py-2 px-4 rounded inline-flex items-center transition duration-300 ${isSending || loading || isUpdating || isEditingLineItem || rcti.xero_submission_status === 'success' ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isSending ? (
                <><FaSync className="animate-spin mr-2" /> Sending...</>
            ) : (
                <><FaPaperPlane className="mr-2" /> Send to Xero</>
            )}
          </button>
          
          {rcti.xero_submission_status === 'success' && rcti.xeroInvoiceId && (
            <button
              onClick={handleSyncFromXero}
              disabled={isSyncing || isUpdating || isEditingLineItem}
              className={`bg-jims-cyan hover:bg-jims-cyan-dark text-white font-bold py-2 px-4 rounded inline-flex items-center transition duration-300 ${isSyncing || isUpdating || isEditingLineItem ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <FaSync className={`mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
              {isSyncing ? 'Syncing...' : 'Sync from Xero'}
            </button>
          )}
        </div>
      )}

      <div className="bg-white shadow-xl rounded-lg p-6 md:p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
          {/* Column 1 */}
          <div>
            <DetailItem label="RCTI ID" value={rcti.id} />
            <DetailItem label="Invoice Number" value={rcti.InvoiceNumber} />
            <DetailItem
              label="Status"
              value={displayStatus ? displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1) : 'N/A'}
              highlight={statusHighlight}
            />
            {canVoid && (
              <div className="mb-2">
                <button
                  onClick={() => setShowVoidConfirm(true)}
                  className="text-red-600 hover:text-red-800 text-sm flex items-center mt-1"
                >
                  <FaBan className="mr-1" size={12} /> Void RCTI
                </button>
              </div>
            )}
            <DetailItem label="Job ID" value={rcti.jobId} linkTo={`/jobs-list/edit/${rcti.jobId}`} />
            <DetailItem label="Reference" value={rcti.Reference} />
            <DetailItem label="RCTI Date" value={formatDate(rcti.RCTIDate)} />
            <DetailItem label="Due Date" value={formatDate(rcti.DueDate)} />
            <DetailItem label="Currency" value={rcti.Currency || 'AUD'} />
            <DetailItem label="Branding Theme" value={rcti.BrandingTheme || 'N/A'} />
          </div>

          {/* Column 2 */}
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3 border-b pb-2">Contact & Address</h3>
            <DetailItem label="Contact Name" value={rcti.SupplierName} />
            <DetailItem label="Email Address" value={rcti.SupplierEmail} />
            <DetailItem label="Phone" value={rcti.SupplierPhone} />
            <DetailItem label="Xero Contact ID" value={rcti.XeroContactId || 'N/A'} />
            <p className="text-sm font-medium text-gray-500 mt-3">Supplier Address:</p>
            <p className="text-gray-800">
                {(rcti.SupplierAddressLine1 || '')}<br />
                {rcti.SupplierAddressLine2 ? <>{rcti.SupplierAddressLine2}<br /></> : ''}
                {rcti.SupplierCity || ''}{rcti.SupplierState ? `, ${rcti.SupplierState}` : ''}<br />
                {rcti.SupplierPostalCode || ''}{rcti.SupplierCountry ? `, ${rcti.SupplierCountry}` : ''}
            </p>
          </div>
        </div>

        {/* RCTI PDF Download Link */}
        {rcti.pdfDownloadUrl && (
          <div className="mt-4">
            <DetailItem label="RCTI PDF" value={
              <button
                onClick={handleViewPdf}
                disabled={isLoadingPdf}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaFilePdf className="mr-1.5" />
                {isLoadingPdf ? 'Opening...' : 'Download PDF'}
              </button>
            } />
          </div>
        )}

        {/* Line Items Section */}
        <div className="mt-6 pt-6 border-t">
            <div className="flex items-center justify-between gap-4 mb-3">
                <h3 className="text-xl font-semibold text-jims-cyan-dark">Line Item</h3>
                {canEditLineItem && !isEditingLineItem && (
                    <button
                        type="button"
                        onClick={() => {
                          setSuccessMessage('');
                          setSendToXeroError(null);
                          setIsEditingLineItem(true);
                        }}
                        className="text-sm font-semibold text-jims-cyan-dark hover:text-jims-cyan"
                    >
                        Edit Line Item
                    </button>
                )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-4">
                <DetailItem label="Description" value={rcti.Description} fullWidth={true} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-4 mt-4">
                {isEditingLineItem ? (
                  <>
                    <div className="mb-2">
                      <p className="text-sm font-medium text-gray-500">Quantity</p>
                      <input
                        type="number"
                        name="Quantity"
                        min="0"
                        step="0.01"
                        value={lineItemForm.Quantity}
                        onChange={handleLineItemInputChange}
                        className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-gray-800 focus:border-jims-cyan focus:outline-none"
                      />
                    </div>
                    <div className="mb-2">
                      <p className="text-sm font-medium text-gray-500">Unit Amount</p>
                      <input
                        type="number"
                        name="UnitAmount"
                        min="0"
                        step="0.01"
                        value={lineItemForm.UnitAmount}
                        onChange={handleLineItemInputChange}
                        className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-gray-800 focus:border-jims-cyan focus:outline-none"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <DetailItem label="Quantity" value={rcti.Quantity} />
                    <DetailItem label="Unit Amount" value={`$${rcti.UnitAmount ? rcti.UnitAmount.toFixed(2) : '0.00'}`} />
                  </>
                )}
                <DetailItem label="Discount" value={rcti.Discount !== null ? `${rcti.Discount}%` : 'N/A'} />
                <DetailItem label="Amount Due" value={`$${amountDue}`} highlight="blue" />
            </div>
            {isEditingLineItem && (
              <div className="mt-4 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCancelLineItemEdit}
                  disabled={isUpdating}
                  className="rounded border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveLineItem}
                  disabled={isUpdating}
                  className="rounded bg-jims-cyan px-4 py-2 text-sm font-semibold text-white hover:bg-jims-cyan-dark disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isUpdating ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
        </div>
        
        {/* Financial & Tracking Details */}
        <div className="mt-6 pt-6 border-t">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Financial & Tracking</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                <DetailItem label="Account Code" value={rcti.AccountCode} />
                <DetailItem label="Tax Type" value={rcti.TaxType} />
                {rcti.TrackingName1 && <DetailItem label={rcti.TrackingName1} value={rcti.TrackingOption1} />}
                {rcti.TrackingName2 && <DetailItem label={rcti.TrackingName2} value={rcti.TrackingOption2} />}
            </div>
        </div>

        {/* Xero Submission Details */}
        {(rcti.xero_submission_status || rcti.xeroInvoiceId) && (
            <div className="mt-6 pt-6 border-t">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Xero Submission Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                    {rcti.xero_submission_status && 
                        <DetailItem 
                            label="Xero Submission Status" 
                            value={rcti.xero_submission_status.charAt(0).toUpperCase() + rcti.xero_submission_status.slice(1)}
                            highlight={rcti.xero_submission_status === 'failed' ? 'red' : rcti.xero_submission_status === 'success' ? 'green' : 'gray'}
                        />
                    }
                    <DetailItem label="Xero Invoice ID" value={rcti.xeroInvoiceId || 'N/A'} />
                </div>
                {rcti.xero_submission_status === 'failed' && rcti.xero_submission_failure_reason && (
                    <div className="mt-4">
                        <DetailItem label="Xero Submission Failure Reason" value={rcti.xero_submission_failure_reason} fullWidth={true} highlight="red-text" />
                    </div>
                )}
            </div>
        )}

        {/* Timestamps */}
        <div className="mt-6 pt-6 border-t text-sm text-gray-500">
            <DetailItem label="Created At" value={formatDateTime(rcti.createdAt)} />
            <DetailItem label="Last Updated At" value={formatDateTime(rcti.updatedAt)} />
        </div>

      </div>

      <ConfirmationModal
        isOpen={showVoidConfirm}
        onClose={() => setShowVoidConfirm(false)}
        onConfirm={handleVoidRCTI}
        title="Void RCTI"
        message={`Are you sure you want to void RCTI ${rcti?.InvoiceNumber || ''}? This action cannot be undone. If the RCTI has been sent to Xero, it will also be voided there.`}
        confirmText="Void RCTI"
        variant="danger"
        isLoading={isVoiding}
        loadingText="Voiding..."
      />
    </div>
  );
};

const DetailItem = ({ label, value, linkTo, highlight, fullWidth }) => {
    const valueClasses = `text-gray-800 ${fullWidth ? 'col-span-full' : ''}`;
    let content = value;
    
    if (linkTo && value !== 'N/A') {
        content = <Link to={linkTo} className="text-green-600 hover:underline">{value}</Link>;
    }
    
    let highlightClass = '';
    if (highlight) {
        switch (highlight) {
            case 'yellow': highlightClass = 'bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full text-xs font-semibold'; break;
            case 'green': highlightClass = 'bg-green-100 text-green-800 px-2 py-0.5 rounded-full text-xs font-semibold'; break;
            case 'blue': highlightClass = 'text-blue-600 font-bold'; break;
            case 'red': highlightClass = 'bg-red-100 text-red-800 px-2 py-0.5 rounded-full text-xs font-semibold'; break;
            case 'red-text': highlightClass = 'text-red-700 font-medium'; break;
            default: highlightClass = 'bg-gray-100 text-gray-800 px-2 py-0.5 rounded-full text-xs font-semibold'; break;
        }
        content = <span className={highlightClass}>{value}</span>;
    }

    return (
        <div className={`mb-2 ${fullWidth ? 'col-span-full' : ''}`}>
            <p className="text-sm font-medium text-gray-500">{label}</p>
            <p className={valueClasses}>{content}</p>
        </div>
    );
};

export default RCTIDetailsPage;
